// Pure helpers for interpreting an avatar-asset-manager rig-upload response.
// Kept dependency-free (no import.meta.env / network) so it's unit-testable.

// ENG-720: the asset-manager now parses the rig on upload and auto-extracts
// materials/textures + a material_mapping (with materialIds) server-side,
// reporting it back as texture_count/material_count/mapping_count. When those
// fields are present the server owns the mapping — the client must NOT also PUT
// its own derived mapping, which is slots-only (no materialId) and would clobber
// the server's. Older backends that merely store the rig omit these fields, so
// the caller falls back to client-side derivation. A 0 count still counts as
// "the server ran" (rig simply had no mesh primitives).
export function serverExtractedMaterials(uploadResponse) {
  return uploadResponse != null && uploadResponse.mapping_count != null
}

// carryOverSummary — what a replace or restore actually KEPT, reported after
// the fact from the server's own count (carried_over_count on the rig-upload /
// restore response; avatar-asset-manager, glb_import.Result).
//
// The pre-upload confirmation can only state the carry-over rule, because the
// client has not parsed the new GLB and does not know its mesh names. This is
// the other end of that: the one number that says what survived, so the user
// does not have to compare slots by eye. Vaelmor_v2's assignments were rebuilt
// by hand twice on 2026-08-28 precisely because nothing reported this.
//
// Returns null rather than a reassuring sentence whenever it has nothing TRUE
// to say — an older backend omits the field entirely, and a character with no
// assignments to begin with has no story worth telling.
//
// `assignedBefore` is the client's own count of bound slots taken BEFORE the
// upload; the server's count is a subset of it, so "12 of 13" is honest even
// though the two numbers come from different places.
export function carryOverSummary(uploadResponse, assignedBefore) {
  const carried = uploadResponse?.carried_over_count
  if (!Number.isFinite(carried) || carried < 0) return null
  const before = Number.isFinite(assignedBefore) ? Math.max(0, Math.trunc(assignedBefore)) : 0
  if (before === 0) return null
  // Clamp: the server counts slots in the NEW mapping, and a disagreement with
  // the client's pre-upload count must not render as "14 of 13".
  const kept = Math.min(Math.trunc(carried), before)
  const noun = before === 1 ? 'material assignment' : 'material assignments'
  if (kept === 0) {
    return `None of the ${before} previous ${noun} carried over — those slots need re-assigning.`
  }
  if (kept >= before) {
    return `All ${before} previous ${noun} carried over.`
  }
  return `${kept} of ${before} previous ${noun} carried over; the rest need re-assigning.`
}
